import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Pencil, ShoppingCart, Truck, CreditCard, Eye } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../lib/api';
import { PageHeader, Button, Card, Input, Select, Modal, Th, Td, EmptyState, Badge } from '../components/ui';
import { fmtMoney, fmtNum, fmtDate, canWrite, canDelete, statusColor } from '../lib/utils';
import { useAuth } from '../context/AuthContext';

export default function Purchases() {
  const { user } = useAuth();
  const write = canWrite(user.role);
  const [tab, setTab] = useState('orders');
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [builder, setBuilder] = useState(false);
  const [supModal, setSupModal] = useState(null);
  const [payPI, setPayPI] = useState(null);
  const [viewPI, setViewPI] = useState(null);
  const [units, setUnits] = useState(['Yard']);

  const load = () => {
    api.get('/purchases').then((r) => setPurchases(r.data));
    api.get('/suppliers').then((r) => setSuppliers(r.data));
    api.get('/raw-materials').then((r) => setMaterials(r.data));
    api.get('/settings').then((r) => setUnits(r.data?.units || ['Yard'])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const delSup = async (id) => { if (!window.confirm('Delete supplier?')) return; await api.delete(`/suppliers/${id}`); toast.success('Deleted'); load(); };
  const delPurchase = async (id) => {
    if (!window.confirm('Delete this purchase invoice? Raw material stock and the linked expense will be reversed.')) return;
    try { await api.delete(`/purchases/${id}`); toast.success('Purchase deleted'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Purchases" subtitle="Purchase invoices & suppliers"
        actions={write && (tab === 'orders'
          ? <Button onClick={() => setBuilder(true)} data-testid="new-purchase-btn"><Plus size={16} /> Purchase Invoice</Button>
          : <Button onClick={() => setSupModal('new')} data-testid="add-supplier-btn"><Plus size={16} /> Supplier</Button>)} />

      <div className="flex gap-1 mb-4 bg-slate-100 p-1 rounded-md w-fit">
        {[['orders', 'Purchase Invoices', ShoppingCart], ['suppliers', 'Suppliers', Truck]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`purchase-tab-${k}`}
            className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded transition-colors ${tab === k ? 'bg-white text-[#1E3A8A] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === 'orders' ? (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr><Th>Invoice #</Th><Th>Date</Th><Th>Supplier</Th><Th>Items</Th><Th right>Total</Th><Th right>Paid</Th><Th right>Due</Th><Th>Status</Th><Th className="text-right">Actions</Th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {purchases.map((p) => {
                  const paid = p.amountPaid ?? p.total;
                  const due = p.dueAmount ?? 0;
                  const status = p.status || 'paid';
                  return (
                  <tr key={p.id} className="hover:bg-slate-50/60 transition-colors" data-testid="purchase-row">
                    <Td><span className="font-mono text-xs font-semibold">{p.purchaseNumber}</span></Td>
                    <Td>{fmtDate(p.date)}</Td>
                    <Td className="font-medium text-slate-800">{p.supplierName}</Td>
                    <Td className="text-xs">{p.items.map((i) => `${i.materialName}${i.rolls ? ` · ${i.rolls} rolls` : ''}${i.yards ? ` · ${i.yards} yd` : (i.quantity && i.unit ? ` · ${i.quantity} ${i.unit}` : (!i.rolls ? ` ×${i.quantity}` : ''))}`).join(', ')}</Td>
                    <Td right className="num">{fmtMoney(p.total)}</Td>
                    <Td right className="num text-emerald-600">{fmtMoney(paid)}</Td>
                    <Td right className={`num font-semibold ${due > 0 ? 'text-rose-600' : 'text-slate-400'}`}>{fmtMoney(due)}</Td>
                    <Td><Badge className={statusColor(status)}>{status}</Badge></Td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => setViewPI(p)} className="text-slate-400 hover:text-[#1E3A8A] p-1" title="View invoice" data-testid="view-purchase-btn"><Eye size={15} /></button>
                      {write && due > 0 && <Button size="sm" variant="success" className="ml-1" onClick={() => setPayPI(p)} data-testid="pay-purchase-btn"><CreditCard size={13} /> Pay</Button>}
                      {canDelete(user.role) && <button onClick={() => delPurchase(p.id)} className="text-slate-400 hover:text-rose-600 p-1 ml-2" data-testid="delete-purchase-btn"><Trash2 size={14} /></button>}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!purchases.length && <EmptyState title="No purchase invoices" icon={ShoppingCart} />}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {suppliers.map((s) => {
            const sp = purchases.filter((p) => p.supplierId === s.id);
            const purchased = sp.reduce((a, p) => a + (p.total || 0), 0);
            const payable = sp.reduce((a, p) => a + (p.dueAmount ?? 0), 0);
            return (
            <Card key={s.id} className="p-4" data-testid="supplier-card">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-heading font-bold text-slate-900">{s.name}</p>
                  <p className="text-xs text-slate-400">{s.contactPerson}</p>
                </div>
                {write && <div className="flex">
                  <button onClick={() => setSupModal(s)} className="text-slate-400 hover:text-blue-600 p-1"><Pencil size={14} /></button>
                  {canDelete(user.role) && <button onClick={() => delSup(s.id)} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 size={14} /></button>}
                </div>}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-[10px] text-slate-400 uppercase">Purchased</p>
                  <p className="text-sm font-semibold num text-slate-800">{fmtNum(purchased)}</p>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-[10px] text-slate-400 uppercase">Paid</p>
                  <p className="text-sm font-semibold num text-emerald-600">{fmtNum(purchased - payable)}</p>
                </div>
                <div className={`rounded p-2 ${payable > 0 ? 'bg-rose-50' : 'bg-slate-50'}`}>
                  <p className={`text-[10px] uppercase ${payable > 0 ? 'text-rose-500' : 'text-slate-400'}`}>Payable</p>
                  <p className={`text-sm font-bold num ${payable > 0 ? 'text-rose-600' : 'text-slate-400'}`} data-testid="supplier-payable">{fmtNum(payable)}</p>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">{s.phone} · {s.email}</p>
              <p className="text-xs text-slate-400 mt-1">{s.address}</p>
            </Card>
            );
          })}
          {!suppliers.length && <EmptyState title="No suppliers" icon={Truck} />}
        </div>
      )}

      {builder && <PurchaseBuilder suppliers={suppliers} materials={materials} units={units} onClose={() => setBuilder(false)} onSaved={() => { setBuilder(false); load(); }} />}
      {supModal && <SupplierModal supplier={supModal === 'new' ? null : supModal} onClose={() => setSupModal(null)} onSaved={() => { setSupModal(null); load(); }} />}
      {payPI && <PayPurchaseModal purchase={payPI} onClose={() => setPayPI(null)} onSaved={() => { setPayPI(null); load(); }} />}
      {viewPI && <ViewPurchaseModal purchase={viewPI} onClose={() => setViewPI(null)} />}
    </div>
  );
}

const EMPTY_ITEM = { materialId: '', materialName: '', custom: false, rolls: '', qty: '', unit: 'Yard', price: 0 };

function PurchaseBuilder({ suppliers, materials, units, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [supplierId, setSupplierId] = useState(suppliers[0]?.id || '');
  const [date, setDate] = useState(today);
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [notes, setNotes] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const setItem = (i, patch) => setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const pick = (i, mid) => {
    if (mid === '__custom') { setItem(i, { materialId: '', materialName: '', custom: true }); return; }
    const m = materials.find((x) => x.id === mid);
    if (m) setItem(i, { materialId: mid, materialName: m.name, custom: false, price: m.purchasePrice });
  };
  const qtyOf = (it) => Number(it.qty) || Number(it.rolls) || 0;
  const total = useMemo(() => items.reduce((s, it) => s + qtyOf(it) * Number(it.price), 0), [items]);
  const paidNum = Math.min(Number(amountPaid) || 0, total);
  const save = async () => {
    const clean = items.filter((it) => (it.materialId || it.materialName.trim()) && qtyOf(it) > 0)
      .map((it) => ({ materialId: it.materialId || null, materialName: it.materialName.trim(), quantity: qtyOf(it), price: Number(it.price), rolls: Number(it.rolls) || 0, unit: it.unit, yards: it.unit === 'Yard' ? Number(it.qty) || 0 : 0 }));
    if (!clean.length) { toast.error('Add items with quantity or rolls'); return; }
    const sup = suppliers.find((s) => s.id === supplierId);
    try {
      await api.post('/purchases', { supplierId, supplierName: sup?.name || '', items: clean, date, notes, amountPaid: paidNum, paymentMethod });
      toast.success('Purchase invoice recorded · stock updated'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open size="lg" title="New Purchase Invoice" onClose={onClose}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-purchase-btn">Save Invoice</Button></>}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Select label="Supplier" value={supplierId} onChange={(e) => setSupplierId(e.target.value)} data-testid="purchase-supplier">
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
          <Input label="Invoice Date" type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} data-testid="purchase-date" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-600 uppercase">Items (Rolls / Materials)</span>
            <Button size="sm" variant="outline" onClick={() => setItems((p) => [...p, { ...EMPTY_ITEM }])} data-testid="add-purchase-item-btn"><Plus size={13} /> Add</Button>
          </div>
          <div className="grid grid-cols-12 gap-2 mb-1 text-[10px] font-semibold text-slate-400 uppercase">
            <div className="col-span-3">Material</div><div className="col-span-2">Rolls</div><div className="col-span-2">Qty</div><div className="col-span-2">Unit</div><div className="col-span-2">Price / Unit</div><div className="col-span-1" />
          </div>
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end">
              <div className="col-span-3">
                {it.custom ? (
                  <Input placeholder="Type material name…" value={it.materialName} onChange={(e) => setItem(i, { materialName: e.target.value })} data-testid="custom-material-input" />
                ) : (
                  <Select value={it.materialId} onChange={(e) => pick(i, e.target.value)}>
                    <option value="">Select material…</option>
                    {materials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    <option value="__custom">＋ Other (type name)…</option>
                  </Select>
                )}
              </div>
              <div className="col-span-2"><Input type="number" step="0.5" placeholder="Rolls" value={it.rolls} onChange={(e) => setItem(i, { rolls: e.target.value })} /></div>
              <div className="col-span-2"><Input type="number" step="0.5" placeholder="Qty" value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} data-testid="purchase-item-qty" /></div>
              <div className="col-span-2">
                <Select value={it.unit} onChange={(e) => setItem(i, { unit: e.target.value })} data-testid="purchase-item-unit">
                  {units.map((u) => <option key={u}>{u}</option>)}
                </Select>
              </div>
              <div className="col-span-2"><Input type="number" step="0.01" placeholder="Price" value={it.price} onChange={(e) => setItem(i, { price: e.target.value })} /></div>
              <div className="col-span-1 pb-2 text-right"><button onClick={() => setItems((p) => p.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-rose-600"><Trash2 size={15} /></button></div>
            </div>
          ))}
          <p className="text-[11px] text-slate-400 mt-1">Line total = price × qty (or × rolls if qty is empty). Qty is in the chosen unit (Yard, Pcs, Nos, Kg…).</p>
        </div>
        <Input label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        <div className="grid grid-cols-2 gap-4">
          <Input label="Amount Paid Now (KWD)" type="number" step="0.001" value={amountPaid} onChange={(e) => setAmountPaid(e.target.value)} data-testid="purchase-amount-paid" />
          <Select label="Payment Method" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
            {['Cash', 'Bank Transfer', 'Cheque', 'KNET'].map((m) => <option key={m}>{m}</option>)}
          </Select>
        </div>
        <div className="flex justify-between items-center text-sm border-t border-slate-100 pt-3">
          <span className="font-semibold">Total: <span className="num text-[#1E3A8A]">{fmtMoney(total)}</span></span>
          <span className={`font-semibold ${total - paidNum > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>Payable to supplier: <span className="num" data-testid="purchase-due-preview">{fmtMoney(Math.max(0, total - paidNum))}</span></span>
        </div>
        <p className="text-xs text-slate-400">Custom-typed materials are added to Inventory automatically. Total is recorded as a raw material expense and reflected in Dashboard accounts.</p>
      </div>
    </Modal>
  );
}

function ViewPurchaseModal({ purchase: p, onClose }) {
  const paid = p.amountPaid ?? p.total;
  const due = p.dueAmount ?? 0;
  return (
    <Modal open size="lg" title={`Purchase Invoice · ${p.purchaseNumber}`} onClose={onClose}
      footer={<Button variant="outline" onClick={onClose}>Close</Button>}>
      <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
        <div><p className="text-xs text-slate-400 uppercase">Date</p><p className="font-medium num">{fmtDate(p.date)}</p></div>
        <div><p className="text-xs text-slate-400 uppercase">Supplier</p><p className="font-medium">{p.supplierName}</p></div>
        <div><p className="text-xs text-slate-400 uppercase">Status</p><Badge className={statusColor(p.status || 'paid')}>{p.status || 'paid'}</Badge></div>
      </div>
      <table className="w-full mb-4">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr><Th>Material</Th><Th right>Rolls</Th><Th right>Qty</Th><Th>Unit</Th><Th right>Price</Th><Th right>Total</Th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {p.items.map((it, i) => (
            <tr key={i} data-testid="view-purchase-item-row">
              <Td className="font-medium text-slate-800">{it.materialName}</Td>
              <Td right className="num">{it.rolls || '—'}</Td>
              <Td right className="num">{fmtNum(it.quantity)}</Td>
              <Td className="text-slate-500">{it.unit || (it.yards ? 'Yard' : '—')}</Td>
              <Td right className="num">{fmtMoney(it.price)}</Td>
              <Td right className="num font-semibold">{fmtMoney(it.total)}</Td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-slate-50 border-t border-slate-200">
          <tr>
            <Td className="font-bold" colSpan="5">Total</Td>
            <Td right className="num font-bold">{fmtMoney(p.total)}</Td>
          </tr>
        </tfoot>
      </table>
      <div className="grid grid-cols-3 gap-2 text-center mb-4">
        <div className="bg-slate-50 rounded p-2"><p className="text-[10px] text-slate-400 uppercase">Total</p><p className="text-sm font-semibold num">{fmtMoney(p.total)}</p></div>
        <div className="bg-emerald-50 rounded p-2"><p className="text-[10px] text-emerald-500 uppercase">Paid</p><p className="text-sm font-semibold num text-emerald-600">{fmtMoney(paid)}</p></div>
        <div className={`rounded p-2 ${due > 0 ? 'bg-rose-50' : 'bg-slate-50'}`}><p className={`text-[10px] uppercase ${due > 0 ? 'text-rose-500' : 'text-slate-400'}`}>Due</p><p className={`text-sm font-bold num ${due > 0 ? 'text-rose-600' : 'text-slate-400'}`}>{fmtMoney(due)}</p></div>
      </div>
      {(p.paymentHistory || []).length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Payment History</p>
          <div className="space-y-1">
            {p.paymentHistory.map((h, i) => (
              <div key={i} className="flex justify-between text-sm border-b border-slate-100 py-1" data-testid="view-purchase-payment-row">
                <span className="text-slate-500">{fmtDate(h.date)} · {h.method}{h.reference ? ` · ${h.reference}` : ''} · by {h.paidBy}</span>
                <span className="num font-semibold text-emerald-600">{fmtMoney(h.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {p.notes && <p className="text-xs text-slate-400 mt-3">Notes: {p.notes}</p>}
    </Modal>
  );
}

function PayPurchaseModal({ purchase, onClose, onSaved }) {
  const [f, setF] = useState({ amount: purchase.dueAmount || 0, method: 'Cash', reference: '' });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      await api.post(`/purchases/${purchase.id}/payments`, { amount: Number(f.amount), method: f.method, reference: f.reference });
      toast.success('Supplier payment recorded'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open size="sm" title={`Pay Supplier · ${purchase.purchaseNumber}`} onClose={onClose}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button variant="success" onClick={save} data-testid="confirm-pay-purchase-btn"><CreditCard size={15} /> Record Payment</Button></>}>
      <p className="text-sm text-slate-500 mb-4">{purchase.supplierName} · Due: <span className="num font-bold text-rose-600">{fmtMoney(purchase.dueAmount)}</span></p>
      <div className="space-y-4">
        <Input label="Amount (KWD)" type="number" step="0.001" value={f.amount} onChange={(e) => set('amount', e.target.value)} data-testid="purchase-payment-amount" />
        <Select label="Method" value={f.method} onChange={(e) => set('method', e.target.value)}>
          {['Cash', 'Bank Transfer', 'Cheque', 'KNET'].map((m) => <option key={m}>{m}</option>)}
        </Select>
        <Input label="Reference" value={f.reference} onChange={(e) => set('reference', e.target.value)} />
      </div>
    </Modal>
  );
}

function SupplierModal({ supplier, onClose, onSaved }) {
  const [f, setF] = useState(supplier || { name: '', contactPerson: '', phone: '', email: '', address: '' });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      if (supplier) await api.put(`/suppliers/${supplier.id}`, f);
      else await api.post('/suppliers', f);
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open size="sm" title={supplier ? 'Edit Supplier' : 'New Supplier'} onClose={onClose}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-supplier-btn">Save</Button></>}>
      <div className="space-y-4">
        <Input label="Name" value={f.name} onChange={(e) => set('name', e.target.value)} data-testid="supplier-name" />
        <Input label="Contact Person" value={f.contactPerson} onChange={(e) => set('contactPerson', e.target.value)} />
        <div className="grid grid-cols-2 gap-4">
          <Input label="Phone" value={f.phone} onChange={(e) => set('phone', e.target.value)} />
          <Input label="Email" value={f.email} onChange={(e) => set('email', e.target.value)} />
        </div>
        <Input label="Address" value={f.address} onChange={(e) => set('address', e.target.value)} />
      </div>
    </Modal>
  );
}
